import * as storage from '../KeyValueStorage';
import { ENV } from '../../config';
import FeatureFlags from '../FeatureFlags';
import { ssFetchJSON } from '../ssFetch';
import analytics from '../analytics';
import { fetchGeoIP } from '../../api/GeoIP';

const COMMISSION_TEST_FLAG: string = 'provider_subscription_commission_20200421';
const UNIVERSAL_COMMISSION_ENABLED_FLAG: string = 'provider_universal_commission_20201215';

// Metro lists are in the 'styleseat-data-files' S3 bucket.
// https://s3.console.aws.amazon.com/s3/buckets/styleseat-data-files/commission-tier-metros/
const METRO_JSON_BASE_URL: string = 'https://d3qcyspl94612g.cloudfront.net/commission-tier-metros';

// NOTE: need both of these due to inconsistency in deploy timing for join page & mobileweb
// making sure no one falls through the cracks
// should be able to remove with next edit to this page
export const CACHE_KEY_OLD: string = 'provider-commission-tier-test-2020';
export const CACHE_KEY: string = 'provider-commission-tier-test-2021';

interface IGeoIPData {
  metro_area: string;
}

export enum ForcedPlanOptions {
  Growth = 'growth',
  Deluxe = 'deluxe',
}

/**
 * Determine if the pro should be in the new commission based pricing tier.
 *
 * Epic: https://styleseat.atlassian.net/browse/EV-2326
 * @param forcedPlan
 * @returns {Promise<boolean>}
 */
export async function inCommissionTierTest(
  forcedPlan?: ForcedPlanOptions,
): Promise<boolean> {
  const envFile = ENV === 'production' ? 'prod' : 'stage';
  const metroUrl = `${METRO_JSON_BASE_URL}/${envFile}.json`;
  try {
    const {
      [COMMISSION_TEST_FLAG]: commissionFlagEnabled,
      [UNIVERSAL_COMMISSION_ENABLED_FLAG]: universalCommissionEnabled,
    } = await FeatureFlags.areEnabled([
      COMMISSION_TEST_FLAG,
      UNIVERSAL_COMMISSION_ENABLED_FLAG,
    ]);

    // Flagged off
    if (!commissionFlagEnabled) {
      await storage.removeItem(CACHE_KEY);
      return false;
    }

    // forced 'growth' plan through queryParam or referring pro's plan
    if (forcedPlan === ForcedPlanOptions.Growth) {
      storage.setItem(CACHE_KEY, true);
      storage.setItem(CACHE_KEY_OLD, true);
      analytics.track('provider_commissiontest_assignment', {
        inTest: true,
        skippedMetroCheck: true,
      });
      // send the event to FB/GA based on forced plan
      analytics.track('provider_commissiontest_assignment_comm');
      return true;
    }
    // forced 'deluxe' plan through queryParam or referring pro's plan
    if (forcedPlan === ForcedPlanOptions.Deluxe) {
      storage.setItem(CACHE_KEY, false);
      storage.setItem(CACHE_KEY_OLD, false);
      analytics.track('provider_commissiontest_assignment', {
        inTest: false,
        skippedMetroCheck: true,
      });
      // send the event to FB/GA based on forced plan
      analytics.track('provider_commissiontest_assignment_sass');
      return false;
    }

    const cachedValue = await storage.getItem(CACHE_KEY);

    if (typeof cachedValue === 'boolean') {
      // send the event to FB/GA based on cachedValue for organic traffic.
      analytics.track(
        cachedValue
          ? 'provider_commissiontest_assignment_comm'
          : 'provider_commissiontest_assignment_sass',
      );
      return cachedValue;
    }

    if (universalCommissionEnabled) {
      storage.setItem(CACHE_KEY, true);
      storage.setItem(CACHE_KEY_OLD, true);
      return true;
    }

    const [metros, geoip]: [Array<string>, IGeoIPData] = await Promise.all([
      ssFetchJSON(metroUrl),
      fetchGeoIP(),
    ]);

    const inTestMetro = metros.includes(geoip.metro_area);
    storage.setItem(CACHE_KEY, inTestMetro);
    storage.setItem(CACHE_KEY_OLD, inTestMetro);
    analytics.track('provider_commissiontest_assignment', {
      inTest: inTestMetro,
      metro: geoip.metro_area,
    });

    return inTestMetro;
  } catch (err) {
    console.error('Error: ', err);
    return false;
  }
}

/**
 * Determine the plan name for provider signup.
 *
 * If the provider is in the commission tier test metro and the flag is enabled, return
 * 'commission'.
 * Otherwise, return 'deluxe'.
 * @returns {Promise<string>}
 */
export async function getProviderSignupPlanName() {
  return (await inCommissionTierTest()) ? 'commission' : 'deluxe';
}
